import { createContext, useLayoutEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getLenis } from './useLenis.js';

gsap.registerPlugin(ScrollTrigger);

// Web fonts swap in after first layout (font-display: swap) and can reflow
// text above a trigger, leaving pinned sections measured against the old
// layout. Re-measure after each batch of fonts lands; the safe refresh is
// debounced and waits for scrolling to stop, the same path ScrollTrigger takes
// on a window resize.
let fontsWatched = false;
function watchFontLoads() {
  if (fontsWatched || !document.fonts) return;
  document.fonts.addEventListener('loadingdone', () => ScrollTrigger.refresh(true));
  fontsWatched = true;
}

// Builds every page animation against the DOM as it is now and returns the
// teardown. Runs only once the page content is in the DOM (see below).
function setupPageAnimations() {
  // Windows ships with the OS "Animation effects" toggle off on a sizeable
  // share of devices (performance profiles, GPO, low-end laptops), which the
  // browser surfaces as `prefers-reduced-motion: reduce`. Old behaviour:
  // bail out of the entire GSAP context → hero headlines never split, page
  // felt totally inert. New behaviour: still run every layout-critical DOM
  // op (the headline split), but skip purely decorative scrubs and replace
  // long Y-translate fades with very short opacity-only fades. This is what
  // the spec actually asks for — "less motion", not "no motion at all".
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const observers = [];
  const ctx = gsap.context(() => {
    /* --- Split [data-split] into words/chars (structural DOM, always) --- */
    document.querySelectorAll('[data-split]').forEach((el) => {
      if (el.dataset.splitDone) return;
      const text = el.textContent || '';
      el.textContent = '';
      el.dataset.splitDone = '1';
      const frag = document.createDocumentFragment();
      text.split(/(\s+)/).forEach((token) => {
        if (token === '') return;
        if (/^\s+$/.test(token)) {
          frag.appendChild(document.createTextNode(' '));
          return;
        }
        const word = document.createElement('span');
        word.className = 'split-word';
        [...token].forEach((ch) => {
          const span = document.createElement('span');
          span.className = 'split-char';
          span.textContent = ch;
          word.appendChild(span);
        });
        frag.appendChild(word);
      });
      el.appendChild(frag);
    });

    const chars = document.querySelectorAll('.split-char');
    if (chars.length) {
      if (reduce) {
        // Brief opacity-only fade so the headline doesn't "pop" in.
        gsap.fromTo(chars, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power1.out' });
      } else {
        gsap.fromTo(
          chars,
          { yPercent: 110, opacity: 0 },
          {
            yPercent: 0,
            opacity: 1,
            duration: 1.1,
            stagger: 0.028,
            ease: 'power3.out',
            delay: 0.15,
          }
        );
      }
    }

    /* --- Hero subtitle / meta fade-up --- */
    gsap.utils.toArray('[data-hero-delay]').forEach((el) => {
      const delay = parseFloat(el.getAttribute('data-hero-delay')) || 0;
      if (reduce) {
        gsap.from(el, {
          opacity: 0,
          duration: 0.25,
          ease: 'power1.out',
          delay: Math.min(delay, 0.2),
        });
      } else {
        gsap.from(el, { y: 30, opacity: 0, duration: 1.1, ease: 'power3.out', delay });
      }
    });

    /* --- Generic .reveal on scroll --- */
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );
    document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
    observers.push(io);

    /* Safety net: in case IO never fires for an in-view reveal (smooth-scroll
       libs / re-mounts / route transitions can sometimes swallow the first
       intersection callback), promote anything currently within the viewport
       on each Lenis scroll tick and once on a short delay. */
    const promoteVisibleReveals = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      document.querySelectorAll('.reveal:not(.is-in)').forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < vh * 0.95) {
          el.classList.add('is-in');
          io.unobserve(el);
        }
      });
    };
    const lenisRef = getLenis();
    if (lenisRef) lenisRef.on('scroll', promoteVisibleReveals);
    const safetyTimer = window.setTimeout(promoteVisibleReveals, 600);
    observers.push({
      disconnect() {
        if (lenisRef) lenisRef.off('scroll', promoteVisibleReveals);
        window.clearTimeout(safetyTimer);
      },
    });

    /* --- Parallax bg images (decorative scrub — skip under reduce-motion) --- */
    if (!reduce) {
      gsap.utils.toArray('[data-parallax]').forEach((el) => {
        const speed = parseFloat(el.getAttribute('data-parallax')) || 0.35;
        gsap.to(el, {
          y: () => -ScrollTrigger.maxScroll(window) * speed * 0.05,
          ease: 'none',
          scrollTrigger: {
            trigger: el.parentElement || el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      });
    }

    /* --- Footer reveal (just a class toggle, fine under reduce-motion) --- */
    document.querySelectorAll('.site-footer').forEach((el) => {
      ScrollTrigger.create({
        trigger: el,
        start: 'top 75%',
        onEnter: () => el.classList.add('is-in'),
      });
    });

    /* --- Artwork/visual scale on scroll (decorative — skip under reduce-motion) --- */
    if (!reduce) {
      gsap.utils
        .toArray('.artwork-visual img, .feature-visual img, .contact-visual img')
        .forEach((img) => {
          gsap.fromTo(
            img,
            { scale: 1.18 },
            {
              scale: 1,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: img,
                start: 'top 85%',
                end: 'center 55%',
                scrub: 0.8,
              },
            }
          );
        });
    }

    /* --- Page transition fade-in (subtle, OK under reduce-motion).
       Use fromTo with explicit start so a strict-mode double-invocation can't
       leave the body parked at opacity:0 if the first cleanup races. --- */
    gsap.fromTo(
      'body',
      { opacity: 0 },
      { opacity: 1, duration: reduce ? 0.2 : 0.6, ease: 'power1.out', overwrite: 'auto' }
    );
  });

  ScrollTrigger.refresh();

  return () => {
    observers.forEach((o) => o.disconnect());
    ctx.revert();
  };
}

// Page animations start once two things hold for the same URL: Layout has
// prepared it (Lenis exists or is known to stay off) and the page's content
// is in the DOM, as reported by PageReady. Whichever report comes last starts
// them. Before they start, the page lands where its URL opens
// (useScrollLanding), so their triggers are measured from there; the landing
// waits for the content too, since an anchor on a lazy page isn't there
// before it, and a new hash on the same page lands it again and leaves the
// animations be. Usually everything happens in one commit: PageReady's layout
// effect runs first (children before parents), then Layout's land the page
// and start the animations, in that order. A lazy page's content can land a
// commit after its URL (a full page load, or the first visit from a page
// without a shared Suspense: Home, `*`); PageReady's report lands the page
// and starts them then. On a full page load with normal motion, Lenis is
// created in a passive effect (useLenis), so Layout reports a commit later
// and, if the content is already in, its report starts them. Either way they
// start in a layout effect and never cause a re-render, so a page's own
// useEffects still run after its first paint.
function createPageAnimations(setup) {
  let layoutPath = null;
  let contentPath = null;
  let landing = null;
  let stop = null;
  const start = () => {
    if (stop === null && layoutPath !== null && layoutPath === contentPath) stop = setup();
  };
  const halt = () => {
    stop?.();
    stop = null;
  };
  const land = () => {
    if (landing === null || landing.path !== contentPath) return;
    const { scroll } = landing;
    landing = null;
    scroll();
  };
  return {
    // `scroll` moves the page to where `path` opens. It runs once that page's
    // content is in; a later request replaces one still waiting.
    landWhenReady(path, scroll) {
      landing = { path, scroll };
      land();
    },
    layoutReady(path) {
      layoutPath = path;
      start();
    },
    layoutGone() {
      layoutPath = null;
      halt();
    },
    contentReady(path) {
      contentPath = path;
      land();
      start();
    },
    contentGone(path) {
      // React runs all of a commit's layout-effect cleanups before any of its
      // new layout effects, so the old page withdraws before the next one
      // reports. Ignore a withdrawal from a page that isn't the one on record.
      if (contentPath !== path) return;
      contentPath = null;
      halt();
    },
  };
}

// How PageReady reaches the page animations Layout owns.
export const PageAnimationsContext = createContext(null);

// The page animations of one Layout, for useScrollLanding, useAnimations and
// (through PageAnimationsContext) PageReady.
export function usePageAnimations() {
  const [animations] = useState(() => createPageAnimations(setupPageAnimations));
  return animations;
}

// Call after useScrollLanding: in a commit that lands the page and starts the
// animations, its layout effect has to land the page first.
export function useAnimations(animations, pathname, lenisReady) {
  useLayoutEffect(() => {
    watchFontLoads();
    // Wait until Lenis exists (or is known to stay off) so the reveal safety
    // net can subscribe to its scroll events.
    if (!lenisReady) return undefined;
    animations.layoutReady(pathname);
    return animations.layoutGone;
  }, [animations, pathname, lenisReady]);
}
